import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Users, Calendar, Save, Edit, Trash2, Plus } from 'lucide-react';

const HedefGirAdmin = () => {
  const [personnel, setPersonnel] = useState([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [targets, setTargets] = useState({});
  const [existingTargets, setExistingTargets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Ürün listesi
  const products = [
    'Trafik Sigortası',
    'Kasko',
    'Sağlık Sigortası',
    'Dask',
    'Konut Sigortası',
    'İsyeri',
    'Yol Yardım',
    'İMM',
    'Ferdi Kaza'
  ];

  // Ay isimleri
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Personelleri yükle
  useEffect(() => {
    fetchPersonnel();
  }, []);

  // Mevcut hedefleri yükle
  useEffect(() => {
    if (selectedPersonnel && selectedMonth && selectedYear) {
      fetchExistingTargets();
    }
  }, [selectedPersonnel, selectedMonth, selectedYear]);

  const fetchPersonnel = async () => {
    try {
      const response = await axios.get('https://centalyayanimda.com/get_personnel1.php', {
        withCredentials: true
      });
      if (response.data.success) {
        setPersonnel(response.data.data);
      } else {
        setMessage('Personel listesi yüklenemedi: ' + response.data.message);
      }
    } catch (error) {
      console.error('Personel listesi yüklenemedi:', error);
      setMessage('Personel listesi yüklenemedi.');
    }
  };

  const fetchExistingTargets = async () => {
    try {
      const response = await axios.get(`https://centalyayanimda.com/get_targets.php?personnel_id=${selectedPersonnel}&month=${selectedMonth}&year=${selectedYear}`);
      setExistingTargets(response.data);
      
      // Mevcut hedefleri forma yükle
      const targetMap = {};
      response.data.forEach(target => {
        targetMap[target.product_name] = target.target_amount;
      });
      setTargets(targetMap);
    } catch (error) {
      console.error('Mevcut hedefler yüklenemedi:', error);
      setExistingTargets([]);
      setTargets({});
    }
  };

  const handleTargetChange = (product, value) => {
    setTargets(prev => ({
      ...prev,
      [product]: value
    }));
  };

  const handleSaveTargets = async () => {
    if (!selectedPersonnel) {
      setMessage('Lütfen bir personel seçin.');
      return;
    }

    setIsLoading(true);
    setMessage('Hedefler kaydediliyor...');

    try {
      const targetData = {
        personnel_id: selectedPersonnel,
        month: selectedMonth,
        year: selectedYear,
        targets: targets
      };

      const response = await axios.post('https://centalyayanimda.com/save_targets.php', targetData);
      
      if (response.data.success) {
        setMessage('Hedefler başarıyla kaydedildi!');
        fetchExistingTargets(); // Yeniden yükle
      } else {
        setMessage('Hedefler kaydedilirken bir hata oluştu: ' + (response.data.message || ''));
      }
    } catch (error) {
      console.error('Hedef kaydetme hatası:', error);
      setMessage('Hedefler kaydedilirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTarget = async (targetId) => {
    if (!window.confirm('Bu hedefi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await axios.delete(`https://centalyayanimda.com/delete_target.php?id=${targetId}`);
      
      if (response.data.success) {
        setMessage('Hedef başarıyla silindi!');
        fetchExistingTargets(); // Yeniden yükle
      } else {
        setMessage('Hedef silinirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Hedef silme hatası:', error);
      setMessage('Hedef silinirken bir hata oluştu.');
    }
  };

  const clearForm = () => {
    setTargets({});
    setSelectedPersonnel('');
    setMessage('');
  };

  const getSelectedPersonnelName = () => {
    const person = personnel.find(p => p.id == selectedPersonnel);
    return person ? person.name : '';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center gap-3 mb-8">
        <Target className="w-8 h-8 text-blue-600" />
        <h1 className="text-3xl font-bold text-gray-800">Personel Hedef Girişi</h1>
      </div>

      {/* Filtreler */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            Personel Seçin
          </label>
          <select
            value={selectedPersonnel}
            onChange={(e) => setSelectedPersonnel(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Personel Seçin</option>
            {personnel.map(person => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Ay
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {months.map((month, index) => (
              <option key={index + 1} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Yıl
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
            <option value={new Date().getFullYear() + 1}>{new Date().getFullYear() + 1}</option>
          </select>
        </div>
      </div>

      {selectedPersonnel && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {getSelectedPersonnelName()} - {months[selectedMonth - 1]} {selectedYear} Hedefleri
          </h2>

          {/* Hedef Girişi */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {products.map(product => (
              <div key={product} className="p-4 border border-gray-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {product}
                </label>
                <input
                  type="number"
                  min="0"
                  value={targets[product] || ''}
                  onChange={(e) => handleTargetChange(product, e.target.value)}
                  placeholder="Hedef adet"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>

          {/* Kaydet Butonları */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={handleSaveTargets}
              disabled={isLoading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Kaydediliyor...' : 'Hedefleri Kaydet'}
            </button>
            <button
              onClick={clearForm}
              className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium"
            >
              Temizle
            </button>
          </div>

          {/* Mevcut Hedefler */}
          {existingTargets.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">Mevcut Hedefler</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ürün</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Hedef Adet</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {existingTargets.map(target => (
                      <tr key={target.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{target.product_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{target.target_amount} adet</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleDeleteTarget(target.id)}
                            className="text-red-600 hover:text-red-800 flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {message && (
        <div className={`p-4 rounded-lg ${message.includes('başarıyla') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default HedefGirAdmin;